import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Chip, 
  CircularProgress, 
  Tabs, 
  Tab,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Button,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  LinearProgress,
  GlobalStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Divider,
  useTheme
} from '@mui/material';
// Import recharts components for visualization
import {
  ResponsiveContainer, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis,
  Radar, 
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell
} from 'recharts';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Helper function to format scores as percentages
const formatScore = (score) => {
  return typeof score === 'number' ? `${Math.round(score * 100)}%` : 'N/A';
};

// Add animation keyframes
const globalStyles = {
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
};

// Helper function to determine color based on score
const getScoreColor = (score) => {
  if (typeof score !== 'number') return 'grey';
  if (score >= 0.9) return 'success.main';
  if (score >= 0.5) return 'warning.main';
  return 'error.main';
};

// Format milliseconds to readable time
const formatTime = (ms) => {
  if (!ms && ms !== 0) return 'N/A';
  
  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`;
  } else {
    return `${(ms / 1000).toFixed(1)}s`;
  }
};

// TabPanel component to display content for each tab
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lighthouse-tabpanel-${index}`}
      aria-labelledby={`lighthouse-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `lighthouse-tab-${index}`,
    'aria-controls': `lighthouse-tabpanel-${index}`,
  };
}

// Format error messages more clearly
const formatErrorMessage = (issues) => {
  if (!issues || issues.length === 0) return null;
  
  if (issues[0]?.error && typeof issues[0].error === 'string') {
    return (
      <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
        {issues[0].error}
      </Alert>
    );
  }
  return null;
};

const LighthouseResults = ({ data, isMobile, techData, issueData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showTimings, setShowTimings] = useState(false);
  const theme = useTheme();
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Ensure we have valid data objects to prevent errors
  const safeData = data || {};
  const safeIssueData = issueData || {};
  
  // Handle different data structures - with better fallbacks for missing data
  let rawData = safeData;
  
  // Check if we need to drill into the data structure
  if (rawData.lighthouseResult) {
    rawData = rawData.lighthouseResult;
  } else if (rawData.rawData && rawData.rawData.lighthouseResult) {
    rawData = rawData.rawData.lighthouseResult;
  }
  
  // Add debugging logs to see the structure of raw data
  console.log('Raw Lighthouse data structure:', 
    Object.keys(rawData).length > 0 ? 
    {
      hasCategories: Boolean(rawData.categories),
      hasLighthouseResult: Boolean(rawData.lighthouseResult),
      categoryKeys: rawData.categories ? Object.keys(rawData.categories) : [],
      topLevelKeys: Object.keys(rawData)
    } : 'Empty object'
  );
  
  // Extract performance score with better fallback
  let performanceScore = 
    (rawData.categories?.performance?.score) || 
    rawData.performance_score || 
    0;
    
  // Extract other category scores with additional fallbacks and debugging
  let accessibilityScore = 
    (rawData.categories?.accessibility?.score) ||
    rawData.accessibility_score ||
    0;
    
  let bestPracticesScore = 
    (rawData.categories?.['best-practices']?.score) ||
    rawData['best-practices_score'] ||
    0;
    
  let seoScore = 
    (rawData.categories?.seo?.score) ||
    rawData.seo_score ||
    0;
    
  let pwaScore = 
    (rawData.categories?.pwa?.score) ||
    rawData.pwa_score ||
    0;
  
  console.log('Extracted scores:', {
    performance: performanceScore,
    accessibility: accessibilityScore,
    bestPractices: bestPracticesScore,
    seo: seoScore,
    pwa: pwaScore
  });
    
  // Create data for radar chart
  const categoryScores = [
    { category: 'Performance', score: Math.round(performanceScore * 100), color: '#0CCE6B' },
    { category: 'Accessibility', score: Math.round(accessibilityScore * 100), color: '#FFA400' },
    { category: 'Best Practices', score: Math.round(bestPracticesScore * 100), color: '#1A73E8' },
    { category: 'SEO', score: Math.round(seoScore * 100), color: '#8C44FF' }
  ];
  
  // Only add PWA category if score is greater than 0
  if (pwaScore > 0) {
    categoryScores.push({ 
      category: 'PWA', 
      score: Math.round(pwaScore * 100), 
      color: '#4CAF50' 
    });
  }
  
  // Main metrics
  const metrics = {};
  
  // Try to extract metrics from different possible structures
  const audits = rawData.lighthouseResult?.audits || rawData.audits || {};
  
  // Logging for debugging
  if (Object.keys(audits).length === 0) {
    console.log('No audits data found in Lighthouse results. Raw data structure:', 
      Object.keys(rawData).length > 0 ? Object.keys(rawData) : 'Empty object');
    
    if (rawData.lighthouseResult) {
      console.log('LighthouseResult keys:', Object.keys(rawData.lighthouseResult));
    }
    
    // Check if the data might be a JSON string directly (outside of raw_data property)
    if (typeof safeData === 'string') {
      try {
        console.log('Entire data appears to be a string, attempting to parse...');
        const parsedData = JSON.parse(safeData);
        
        // Try to extract category scores
        if (parsedData.categories) {
          if (!accessibilityScore && parsedData.categories.accessibility?.score) {
            console.log('Extracted accessibility score from direct string parsing');
            accessibilityScore = parsedData.categories.accessibility.score;
            categoryScores[1].score = Math.round(accessibilityScore * 100);
          }
          
          if (!bestPracticesScore && 
              (parsedData.categories['best-practices']?.score || parsedData.categories.best_practices?.score)) {
            console.log('Extracted best practices score from direct string parsing');
            bestPracticesScore = parsedData.categories['best-practices']?.score || 
                                parsedData.categories.best_practices?.score;
            categoryScores[2].score = Math.round(bestPracticesScore * 100);
          }
          
          if (!seoScore && parsedData.categories.seo?.score) {
            console.log('Extracted SEO score from direct string parsing');
            seoScore = parsedData.categories.seo.score;
            categoryScores[3].score = Math.round(seoScore * 100);
          }
        }
        
        // Check for audits in the parsed data
        if (parsedData.lighthouseResult?.audits) {
          console.log('Found audits in directly parsed string');
          Object.assign(audits, parsedData.lighthouseResult.audits);
        } else if (parsedData.audits) {
          console.log('Found audits directly in parsed string');
          Object.assign(audits, parsedData.audits);
        }
      } catch (e) {
        console.error('Failed to parse data as direct JSON string:', e);
      }
    }
    
    // Check if raw_data is a string and try to parse it
    if (rawData.raw_data && typeof rawData.raw_data === 'string') {
      try {
        console.log('Found raw_data as string, attempting to parse...');
        const parsedRawData = JSON.parse(rawData.raw_data);
        
        // Try to extract category scores if they weren't found earlier
        if (!accessibilityScore && parsedRawData.categories?.accessibility?.score) {
          console.log('Extracted accessibility score from parsed raw_data');
          accessibilityScore = parsedRawData.categories.accessibility.score;
        }
        
        if (!bestPracticesScore && 
            (parsedRawData.categories?.['best-practices']?.score || 
             parsedRawData.categories?.best_practices?.score)) {
          console.log('Extracted best practices score from parsed raw_data');
          bestPracticesScore = parsedRawData.categories?.['best-practices']?.score || 
                              parsedRawData.categories?.best_practices?.score;
        }
        
        if (!seoScore && parsedRawData.categories?.seo?.score) {
          console.log('Extracted SEO score from parsed raw_data');
          seoScore = parsedRawData.categories.seo.score;
        }
        
        // Update categoryScores with any newly found values
        categoryScores[1].score = Math.round(accessibilityScore * 100);
        categoryScores[2].score = Math.round(bestPracticesScore * 100);
        categoryScores[3].score = Math.round(seoScore * 100);
        
        // Also check for audits in the parsed data
        if (parsedRawData.lighthouseResult?.audits) {
          console.log('Successfully parsed raw_data containing audits!');
          // Override the audits with the parsed data
          Object.assign(audits, parsedRawData.lighthouseResult.audits);
        } else if (parsedRawData.audits) {
          console.log('Successfully parsed raw_data containing audits directly!');
          Object.assign(audits, parsedRawData.audits);
        }
      } catch (e) {
        console.error('Failed to parse raw_data string:', e);
      }
    }
  }
  
  console.log('Audits found:', Object.keys(audits).length);
  
  // Common metrics to display
  const keysToExtract = [
    'first-contentful-paint',
    'largest-contentful-paint',
    'total-blocking-time',
    'cumulative-layout-shift',
    'speed-index',
    'interactive'
  ];
  
  // Extract metrics from structure
  keysToExtract.forEach(key => {
    const audit = audits[key];
    if (audit) {
      metrics[key] = {
        title: audit.title || key,
        value: audit.displayValue || formatTime(audit.numericValue),
        score: audit.score,
        numericValue: audit.numericValue
      };
    }
  });
  
  // Detect if we have metrics data
  const hasMetrics = Object.keys(metrics).length > 0;
  
  // Get opportunities for improvement
  let opportunities = [];
  try {
    opportunities = Object.entries(audits)
      .filter(([id, audit]) => {
        const isOpportunity = audit && 
                           audit.score !== undefined && 
                           audit.score < 0.9 && 
                           audit.details?.type === 'opportunity';
        
        return isOpportunity;
      })
      .map(([id, audit]) => {
        // Extract savings value for sorting
        const savings = audit.details?.overallSavingsMs || 
                       audit.details?.overallSavingsBytes || 
                       0;
        
        return {
          id,
          title: audit.title || id,
          description: audit.description || '',
          score: audit.score || 0,
          displayValue: audit.displayValue || formatScore(audit.score),
          savings: savings,
          details: audit.details || {}
        };
      })
      // Sort by savings in descending order
      .sort((a, b) => b.savings - a.savings);
      
    console.log(`Extracted ${opportunities.length} opportunities`);
  } catch (error) {
    console.error('Error extracting opportunities:', error);
  }
  
  // Get diagnostics with expanded filter criteria
  let diagnostics = [];
  try {
    diagnostics = Object.entries(audits)
      .filter(([id, audit]) => {
        // Include more types of diagnostics
        const isDiagnostic = (audit.details?.type === 'diagnostic' || 
                           id.includes('diagnostic') ||
                           audit.details?.type === 'table' ||
                           audit.details?.type === 'debugdata') &&
                           audit.scoreDisplayMode !== 'informative';
        return isDiagnostic;
      })
      .map(([id, audit]) => {
        return {
          id,
          title: audit.title || id,
          description: audit.description || '',
          score: audit.score,
          displayValue: audit.displayValue
        };
      });
      
    // If we still have no diagnostics, try a more lenient approach
    if (diagnostics.length === 0) {
      console.log('No diagnostics found with standard filter, trying expanded criteria...');
      
      diagnostics = Object.entries(audits)
        .filter(([id, audit]) => {
          // Catch more potential diagnostics
          return audit && 
                !audit.score && // Items that don't have scores are often diagnostics
                audit.scoreDisplayMode !== 'binary' &&
                audit.title && 
                !id.includes('content') && // Exclude content-related audits
                !id.startsWith('uses-');   // Exclude optimization audits
        })
        .map(([id, audit]) => ({
          id,
          title: audit.title || id,
          description: audit.description || '',
          score: null,
          displayValue: audit.displayValue
        }));
    }
    
    console.log(`Extracted ${diagnostics.length} diagnostics`);
  } catch (error) {
    console.error('Error extracting diagnostics:', error);
  }
  
  // Get passed audits
  let passedAudits = [];
  try {
    passedAudits = Object.entries(audits)
      .filter(([id, audit]) => {
        // Expanded criteria to identify passed audits
        const isPassed = audit && 
                      (audit.score === 1 || 
                       (audit.scoreDisplayMode === 'binary' && audit.score === 1) ||
                       (audit.scoreDisplayMode === 'informative' && audit.score === 1) ||
                       (audit.scoreDisplayMode === 'notApplicable') || 
                       (audit.scoreDisplayMode === 'manual' && !audit.score) ||
                       (audit.id && !keysToExtract.includes(audit.id) && 
                        !id.includes('performance') && 
                        !id.includes('metrics') && 
                        audit.title && 
                        !audit.details?.items && 
                        audit.score === 1));
        
        // Exclude audits that are already in opportunities or diagnostics
        const isInOpportunities = opportunities.some(o => o.id === id);
        const isInDiagnostics = diagnostics.some(d => d.id === id);
        
        return isPassed && !isInOpportunities && !isInDiagnostics;
      })
      .map(([id, audit]) => ({
        id,
        title: audit.title || id,
        description: audit.description || '',
        category: audit.scoreDisplayMode || 'binary'
      }));
      
    // If we still don't have passed audits, try a more lenient approach
    if (passedAudits.length === 0) {
      console.log('No passed audits found with standard filter, trying expanded criteria...');
      
      // Look for any item that's not in opportunities or diagnostics and has a good score
      passedAudits = Object.entries(audits)
        .filter(([id, audit]) => {
          if (!audit || !audit.title) return false;
          
          // Skip items already in other categories
          const isInOpportunities = opportunities.some(o => o.id === id);
          const isInDiagnostics = diagnostics.some(d => d.id === id);
          if (isInOpportunities || isInDiagnostics) return false;
          
          // Include "good" items 
          return (audit.score === 1 || 
                 audit.scoreDisplayMode === 'notApplicable' || 
                 audit.scoreDisplayMode === 'informative');
        })
        .map(([id, audit]) => ({
          id,
          title: audit.title || id,
          description: audit.description || '',
          category: audit.scoreDisplayMode || 'binary'
        }));
    }
    
    console.log(`Extracted ${passedAudits.length} passed audits`);
  } catch (error) {
    console.error('Error extracting passed audits:', error);
  }
  
  // Extract broken links
  let brokenLinks = [];
  if (safeIssueData && Array.isArray(safeIssueData.brokenLinks)) {
    brokenLinks = safeIssueData.brokenLinks;
  } else if (safeIssueData && Array.isArray(safeIssueData.issues)) {
    brokenLinks = safeIssueData.issues;
  } else {
    // Try to extract from audits if available
    const brokenLinksAudit = Object.entries(audits)
      .filter(([id, _]) => id === 'broken-links')
      .map(([_, audit]) => audit);
      
    if (brokenLinksAudit.length > 0 && 
        brokenLinksAudit[0] && 
        brokenLinksAudit[0].details && 
        Array.isArray(brokenLinksAudit[0].details.items)) {
      brokenLinks = brokenLinksAudit[0].details.items.map(item => ({
        url: item.url || item.source || '',
        statusCode: item.statusCode || 'Unknown',
        text: item.text || '',
        error: item.statusCode >= 400 ? `HTTP ${item.statusCode}` : null
      }));
    }
  }
  
  // Make absolutely sure brokenLinks is an array
  if (!Array.isArray(brokenLinks)) {
    console.error('Expected brokenLinks to be an array but got:', typeof brokenLinks);
    brokenLinks = [];
  }
  
  // Extract script issues
  let scriptIssues = [];
  if (safeIssueData && Array.isArray(safeIssueData.scriptIssues)) {
    scriptIssues = safeIssueData.scriptIssues;
  } else {
    // Try to extract from audits if available
    const scriptAudits = Object.entries(audits)
      .filter(([id, _]) => 
        id.includes('script') || 
        id.includes('js') || 
        id === 'render-blocking-resources'
      )
      .map(([_, audit]) => audit)
      .filter(audit => audit && audit.details && Array.isArray(audit.details.items));
      
    if (scriptAudits.length > 0) {
      scriptIssues = scriptAudits.flatMap(audit => 
        audit.details.items.map(item => ({
          url: item.url || item.src || '',
          type: 'script',
          size: item.totalBytes || item.size || 0,
          blockingTime: item.blockingTime || 0,
          issueType: audit.id || 'script-issue',
          description: audit.title || 'Script issue'
        }))
      );
    }
  }
  
  // Make sure scriptIssues is an array
  if (!Array.isArray(scriptIssues)) {
    console.error('Expected scriptIssues to be an array but got:', typeof scriptIssues);
    scriptIssues = [];
  }
  
  // Extract image issues
  let imageIssues = [];
  if (safeIssueData && Array.isArray(safeIssueData.imageIssues)) {
    imageIssues = safeIssueData.imageIssues;
  } else {
    // Try to extract from audits if available
    const imageAudits = Object.entries(audits)
      .filter(([id, _]) => 
        id.includes('image') || 
        id === 'uses-responsive-images' || 
        id === 'offscreen-images'
      )
      .map(([_, audit]) => audit)
      .filter(audit => audit && audit.details && Array.isArray(audit.details.items));
      
    if (imageAudits.length > 0) {
      imageIssues = imageAudits.flatMap(audit => 
        audit.details.items.map(item => ({
          url: item.url || item.src || '',
          type: 'image',
          size: item.totalBytes || item.size || 0,
          wastedBytes: item.wastedBytes || 0,
          issueType: audit.id || 'image-issue',
          description: audit.title || 'Image issue'
        }))
      );
    }
  }
  
  // Make sure imageIssues is an array
  if (!Array.isArray(imageIssues)) {
    console.error('Expected imageIssues to be an array but got:', typeof imageIssues);
    imageIssues = [];
  }
  
  return (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      <GlobalStyles styles={globalStyles} />
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Performance Analysis Results
        </Typography>
        <Typography 
          variant="body2" 
          color="textSecondary"
          component="div"
        >
          {safeData?.url || 'Website analysis'}
          {safeData?.mobile !== undefined && (
            <Chip 
              label={safeData.mobile ? 'Mobile' : 'Desktop'} 
              size="small" 
              color="primary" 
              variant="outlined" 
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            '& .MuiTab-root': {
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 48,
              textTransform: 'none'
            }
          }}
        >
          {/* Overview - index 0 */}
          <Tab label={<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Overview</Typography>} {...a11yProps(0)} />
          
          {/* Opportunities - index 1 */}
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 'inherit', fontWeight: 'inherit' }}>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Opportunities</Typography>
                <Chip 
                  size="small" 
                  label={opportunities?.length || 0} 
                  color={opportunities?.length > 0 ? "warning" : "success"} 
                  sx={{ ml: 1 }} 
                />
              </Box>
            } 
            {...a11yProps(1)} 
          />
          
          {/* Broken Links - index 2 */}
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 'inherit', fontWeight: 'inherit' }}>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Broken Links</Typography>
                <Chip size="small" label={brokenLinks?.length || 0} color={brokenLinks?.length > 0 ? "error" : "success"} sx={{ ml: 1 }} />
              </Box>
            }
            {...a11yProps(2)} 
          />
          
          {/* Script Issues - index 3 */}
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 'inherit', fontWeight: 'inherit' }}>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Script Issues</Typography>
                <Chip size="small" label={scriptIssues?.length || 0} color={scriptIssues?.length > 0 ? "warning" : "success"} sx={{ ml: 1 }} />
              </Box>
            }
            {...a11yProps(3)} 
          />
          
          {/* Image Issues - index 4 */}
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 'inherit', fontWeight: 'inherit' }}>
                <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Image Issues</Typography>
                <Chip size="small" label={imageIssues?.length || 0} color={imageIssues?.length > 0 ? "warning" : "success"} sx={{ ml: 1 }} />
              </Box>
            }
            {...a11yProps(4)} 
          />
          
          {/* Timeline - index 5 */}
          <Tab label={<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Timeline</Typography>} {...a11yProps(5)} />
          
          {/* Screenshot - index 6 */}
          <Tab label={<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Screenshot</Typography>} {...a11yProps(6)} />
          
          {/* Diagnostics - index 7 */}
          <Tab label={<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Diagnostics</Typography>} {...a11yProps(7)} />
          
          {/* Passed Audits - index 8 */}
          <Tab label={<Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>Passed Audits</Typography>} {...a11yProps(8)} />
        </Tabs>
      </Box>

      {/* Overview Tab Panel - index 0 */}
      <TabPanel value={activeTab} index={0}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Lighthouse Performance Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Based on Google Lighthouse scoring methodology. Analysis for: {safeData?.url || 'Unknown URL'}
          </Typography>
          
          {/* Performance Summary Box */}
          <Paper 
            elevation={0} 
            sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: performanceScore >= 0.9 ? 'success.50' : 
                        performanceScore >= 0.5 ? 'warning.50' : 'error.50',
              border: 1,
              borderColor: performanceScore >= 0.9 ? 'success.200' : 
                            performanceScore >= 0.5 ? 'warning.200' : 'error.200',
              borderRadius: 2,
              animation: 'fadeIn 0.8s ease-out'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {performanceScore >= 0.9 ? (
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20, mr: 1 }} />
              ) : performanceScore >= 0.5 ? (
                <InfoOutlinedIcon color="warning" sx={{ fontSize: 20, mr: 1 }} />
              ) : (
                <ErrorOutlineIcon color="error" sx={{ fontSize: 20, mr: 1 }} />
              )}
              <Typography variant="subtitle2">
                {performanceScore >= 0.9 
                  ? 'This site has excellent performance metrics!' 
                  : performanceScore >= 0.7
                    ? 'This site has good performance metrics with some room for improvement.' 
                    : performanceScore >= 0.5
                      ? 'This site has average performance metrics that could use improvement.'
                      : 'This site has poor performance metrics that need significant improvement.'}
              </Typography>
            </Box>
            {performanceScore < 0.9 && (
              <Typography variant="body2" sx={{ mt: 1, ml: 4 }}>
                {performanceScore >= 0.7 
                  ? 'Focus on optimizing the largest contentful paint and total blocking time for even better results.'
                  : performanceScore >= 0.5
                    ? 'Consider optimizing images, reducing JavaScript, and improving server response time.'
                    : 'Major optimizations are needed including: reducing JavaScript, optimizing images, implementing proper caching, and improving server response time.'}
              </Typography>
            )}
          </Paper>
        </Box>
        
        {/* Lighthouse Categories Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Performance Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Performance
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: 'grey.200',
                      position: 'absolute'
                    }}
                  />
                  <CircularProgress 
                    variant="determinate" 
                    value={performanceScore * 100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: getScoreColor(performanceScore),
                      circle: { strokeLinecap: 'round' },
                      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                  <Box
                    sx={{
                      top: 0, left: 0, bottom: 0, right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color={getScoreColor(performanceScore)}
                      fontWeight="bold"
                    >
                      {Math.round(performanceScore * 100)}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  size="small" 
                  label={performanceScore >= 0.9 ? "Good" : performanceScore >= 0.5 ? "Needs Improvement" : "Poor"}
                  sx={{ 
                    bgcolor: getScoreColor(performanceScore), 
                    color: 'white',
                    mt: 1
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          {/* Accessibility Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Accessibility
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: 'grey.200',
                      position: 'absolute'
                    }}
                  />
                  <CircularProgress 
                    variant="determinate" 
                    value={accessibilityScore * 100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: getScoreColor(accessibilityScore),
                      circle: { strokeLinecap: 'round' },
                      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                  <Box
                    sx={{
                      top: 0, left: 0, bottom: 0, right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color={getScoreColor(accessibilityScore)}
                      fontWeight="bold"
                    >
                      {Math.round(accessibilityScore * 100)}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  size="small" 
                  label={accessibilityScore >= 0.9 ? "Good" : accessibilityScore >= 0.5 ? "Needs Improvement" : "Poor"}
                  sx={{ 
                    bgcolor: getScoreColor(accessibilityScore), 
                    color: 'white',
                    mt: 1
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          {/* Best Practices Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Best Practices
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: 'grey.200',
                      position: 'absolute'
                    }}
                  />
                  <CircularProgress 
                    variant="determinate" 
                    value={bestPracticesScore * 100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: getScoreColor(bestPracticesScore),
                      circle: { strokeLinecap: 'round' },
                      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                  <Box
                    sx={{
                      top: 0, left: 0, bottom: 0, right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color={getScoreColor(bestPracticesScore)}
                      fontWeight="bold"
                    >
                      {Math.round(bestPracticesScore * 100)}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  size="small" 
                  label={bestPracticesScore >= 0.9 ? "Good" : bestPracticesScore >= 0.5 ? "Needs Improvement" : "Poor"}
                  sx={{ 
                    bgcolor: getScoreColor(bestPracticesScore), 
                    color: 'white',
                    mt: 1
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          {/* SEO Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  SEO
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: 'grey.200',
                      position: 'absolute'
                    }}
                  />
                  <CircularProgress 
                    variant="determinate" 
                    value={seoScore * 100} 
                    size={80}
                    thickness={4}
                    sx={{ 
                      color: getScoreColor(seoScore),
                      circle: { strokeLinecap: 'round' },
                      transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                  <Box
                    sx={{
                      top: 0, left: 0, bottom: 0, right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      color={getScoreColor(seoScore)}
                      fontWeight="bold"
                    >
                      {Math.round(seoScore * 100)}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  size="small" 
                  label={seoScore >= 0.9 ? "Good" : seoScore >= 0.5 ? "Needs Improvement" : "Poor"}
                  sx={{ 
                    bgcolor: getScoreColor(seoScore), 
                    color: 'white',
                    mt: 1
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          
          {/* Make PWA score section conditional based on whether PWA data exists */}
          {pwaScore > 0 && (
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={0}
                sx={{ 
                  borderRadius: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    PWA
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-flex', my: 1 }}>
                    <CircularProgress 
                      variant="determinate" 
                      value={100} 
                      size={80}
                      thickness={4}
                      sx={{ 
                        color: 'grey.200',
                        position: 'absolute'
                      }}
                    />
                    <CircularProgress 
                      variant="determinate" 
                      value={pwaScore * 100} 
                      size={80}
                      thickness={4}
                      sx={{ 
                        color: getScoreColor(pwaScore),
                        circle: { strokeLinecap: 'round' },
                        transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                    <Box
                      sx={{
                        top: 0, left: 0, bottom: 0, right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="div"
                        color={getScoreColor(pwaScore)}
                        fontWeight="bold"
                      >
                        {Math.round(pwaScore * 100)}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip 
                    size="small" 
                    label={pwaScore >= 0.9 ? "Good" : pwaScore >= 0.5 ? "Needs Improvement" : "Poor"}
                    sx={{ 
                      bgcolor: getScoreColor(pwaScore), 
                      color: 'white',
                      mt: 1
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
        
        {/* Categories Visualization */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={5}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%'
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Lighthouse Score Overview
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={categoryScores}>
                      <PolarGrid stroke="#e0e0e0" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#666', fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#0CCE6B"
                        fill="#0CCE6B"
                        fillOpacity={0.6}
                        animationDuration={1000}
                        animationEasing="ease-in-out"
                      />
                      <RechartsTooltip formatter={(value) => [`${value}%`, 'Score']} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                
                {/* Add a bar chart comparison */}
                <Box sx={{ height: 200, mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Score Comparison
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={categoryScores}
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} />
                      <RechartsTooltip formatter={(value) => [`${value}%`, 'Score']} />
                      <Bar 
                        dataKey="score" 
                        name="Score" 
                        fill="#8884d8" 
                        radius={[4, 4, 0, 0]}
                        barSize={30}
                        animationDuration={1200}
                        animationEasing="ease-out"
                      >
                        {
                          categoryScores.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))
                        }
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card 
              elevation={0}
              sx={{ 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                height: '100%'
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Performance Score Breakdown
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Performance score is weighted as follows:
                  </Typography>
                  
                  {/* LCP */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">Largest Contentful Paint (25%)</Typography>
                      <Typography variant="body2" color={getScoreColor(metrics['largest-contentful-paint']?.score || 0)}>
                        {metrics['largest-contentful-paint']?.value || 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(metrics['largest-contentful-paint']?.score || 0) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        transition: 'all 1s ease',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(metrics['largest-contentful-paint']?.score || 0),
                          borderRadius: 1,
                          transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  </Box>
                  
                  {/* TBT */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">Total Blocking Time (30%)</Typography>
                      <Typography variant="body2" color={getScoreColor(metrics['total-blocking-time']?.score || 0)}>
                        {metrics['total-blocking-time']?.value || 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(metrics['total-blocking-time']?.score || 0) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        transition: 'all 1s ease',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(metrics['total-blocking-time']?.score || 0),
                          borderRadius: 1
                        }
                      }}
                    />
                  </Box>
                  
                  {/* CLS */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">Cumulative Layout Shift (25%)</Typography>
                      <Typography variant="body2" color={getScoreColor(metrics['cumulative-layout-shift']?.score || 0)}>
                        {metrics['cumulative-layout-shift']?.value || 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(metrics['cumulative-layout-shift']?.score || 0) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(metrics['cumulative-layout-shift']?.score || 0),
                          borderRadius: 1,
                          transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  </Box>
                  
                  {/* FCP */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">First Contentful Paint (10%)</Typography>
                      <Typography variant="body2" color={getScoreColor(metrics['first-contentful-paint']?.score || 0)}>
                        {metrics['first-contentful-paint']?.value || 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(metrics['first-contentful-paint']?.score || 0) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        transition: 'all 1s ease',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(metrics['first-contentful-paint']?.score || 0),
                          borderRadius: 1,
                          transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  </Box>
                  
                  {/* SI */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2">Speed Index (10%)</Typography>
                      <Typography variant="body2" color={getScoreColor(metrics['speed-index']?.score || 0)}>
                        {metrics['speed-index']?.value || 'N/A'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(metrics['speed-index']?.score || 0) * 100} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        transition: 'all 1s ease',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getScoreColor(metrics['speed-index']?.score || 0),
                          borderRadius: 1,
                          transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Performance Metrics Details */}
        {hasMetrics && (
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 2, 
              border: 1, 
              borderColor: 'divider', 
              mb: 4
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Core Web Vitals & Other Metrics
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  {/* LCP */}
                  {metrics['largest-contentful-paint'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['largest-contentful-paint'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Largest Contentful Paint
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['largest-contentful-paint'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['largest-contentful-paint'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['largest-contentful-paint'].score >= 0.9 ? 'Good: < 2.5s' : 
                           metrics['largest-contentful-paint'].score >= 0.5 ? 'Needs Improvement: 2.5s - 4s' : 
                           'Poor: > 4s'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* CLS */}
                  {metrics['cumulative-layout-shift'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['cumulative-layout-shift'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Cumulative Layout Shift
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['cumulative-layout-shift'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['cumulative-layout-shift'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['cumulative-layout-shift'].score >= 0.9 ? 'Good: < 0.1' : 
                           metrics['cumulative-layout-shift'].score >= 0.5 ? 'Needs Improvement: 0.1 - 0.25' : 
                           'Poor: > 0.25'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* TBT */}
                  {metrics['total-blocking-time'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['total-blocking-time'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Total Blocking Time
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['total-blocking-time'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['total-blocking-time'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['total-blocking-time'].score >= 0.9 ? 'Good: < 200ms' : 
                           metrics['total-blocking-time'].score >= 0.5 ? 'Needs Improvement: 200ms - 600ms' : 
                           'Poor: > 600ms'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* FCP */}
                  {metrics['first-contentful-paint'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['first-contentful-paint'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          First Contentful Paint
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['first-contentful-paint'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['first-contentful-paint'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['first-contentful-paint'].score >= 0.9 ? 'Good: < 1.8s' : 
                           metrics['first-contentful-paint'].score >= 0.5 ? 'Needs Improvement: 1.8s - 3s' : 
                           'Poor: > 3s'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Speed Index */}
                  {metrics['speed-index'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['speed-index'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Speed Index
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['speed-index'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['speed-index'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['speed-index'].score >= 0.9 ? 'Good: < 3.4s' : 
                           metrics['speed-index'].score >= 0.5 ? 'Needs Improvement: 3.4s - 5.8s' : 
                           'Poor: > 5.8s'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {/* TTI */}
                  {metrics['interactive'] && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: getScoreColor(metrics['interactive'].score),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Time to Interactive
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                          <Typography 
                            variant="h6" 
                            component="span"
                            color={getScoreColor(metrics['interactive'].score)}
                            fontWeight="medium"
                            sx={{ mr: 1 }}
                          >
                            {metrics['interactive'].value}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {metrics['interactive'].score >= 0.9 ? 'Good: < 3.8s' : 
                           metrics['interactive'].score >= 0.5 ? 'Needs Improvement: 3.8s - 7.3s' : 
                           'Poor: > 7.3s'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {/* Lighthouse Scoring Info */}
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2, 
            border: 1, 
            borderColor: 'divider', 
            mb: 2
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Understanding Lighthouse Scores
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Score Ranges
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main', mr: 1 }} />
                        <Typography variant="body2">90-100: Good</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main', mr: 1 }} />
                        <Typography variant="body2">50-89: Needs Improvement</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main', mr: 1 }} />
                        <Typography variant="body2">0-49: Poor</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    About Lighthouse Scoring
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Lighthouse score weightings are based on real-world performance data. Scores are determined by a log-normal distribution of metric values from HTTP Archive.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Category Explanations */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Score Categories
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#0CCE6B', mr: 1 }} />
                    <Typography variant="body2"><strong>Performance:</strong> Page loading speed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FFA400', mr: 1 }} />
                    <Typography variant="body2"><strong>Accessibility:</strong> WCAG compliance</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1A73E8', mr: 1 }} />
                    <Typography variant="body2"><strong>Best Practices:</strong> Web standards</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#8C44FF', mr: 1 }} />
                    <Typography variant="body2"><strong>SEO:</strong> Search optimization</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4CAF50', mr: 1 }} />
                    <Typography variant="body2"><strong>PWA:</strong> Progressive Web App features</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>
      
      {/* Opportunities Tab Panel - index 1 */}
      <TabPanel value={activeTab} index={1}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Opportunities for Improvement
            <Tooltip title="Sorted by potential impact">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Typography>
          
          {opportunities.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'success.light', 
                color: 'success.dark',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                Great job! No improvement opportunities were found.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {opportunities.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: { xs: 2, sm: 3 }, // Responsive padding
                      border: 1, 
                      borderColor: 'divider',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 1
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile, row on desktop
                      justifyContent: 'space-between', 
                      gap: { xs: 2, sm: 3 }
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}> {/* minWidth: 0 prevents flex item from overflowing */}
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom 
                          sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            wordBreak: 'break-word' // Allows long words to break
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            mb: item.details?.items?.length > 0 ? 2 : 0
                          }}
                        >
                          {item.description}
                        </Typography>
                        
                        {/* Show details if available */}
                        {item.details?.items?.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block', 
                                mb: 1,
                                fontWeight: 500
                              }}
                            >
                              Affected Resources:
                            </Typography>
                            <Box 
                              sx={{ 
                                maxHeight: 150, 
                                overflowY: 'auto',
                                px: 1,
                                '&::-webkit-scrollbar': {
                                  width: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  background: 'rgba(0, 0, 0, 0.1)',
                                  borderRadius: '4px',
                                },
                              }}
                            >
                              {item.details.items.slice(0, 5).map((detail, idx) => (
                                <Typography 
                                  key={idx} 
                                  variant="caption" 
                                  component="div"
                                  sx={{ 
                                    mb: 0.5,
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    wordBreak: 'break-all' // Ensures long URLs break
                                  }}
                                >
                                  <span style={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    backgroundColor: theme.palette.primary.main,
                                    display: 'inline-block',
                                    marginTop: '6px'
                                  }} />
                                  <span style={{ flex: 1 }}>
                                    {detail.url || detail.source || detail.location || 'Resource ' + (idx + 1)}
                                  </span>
                                </Typography>
                              ))}
                              {item.details.items.length > 5 && (
                                <Typography 
                                  variant="caption" 
                                  color="primary"
                                  sx={{ 
                                    display: 'block',
                                    mt: 1,
                                    fontWeight: 500
                                  }}
                                >
                                  +{item.details.items.length - 5} more items
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'row', sm: 'column' },
                        alignItems: { xs: 'center', sm: 'flex-end' },
                        gap: { xs: 2, sm: 1 },
                        justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                        minWidth: { xs: 'auto', sm: '120px' },
                        flexShrink: 0
                      }}>
                        <Chip 
                          label={item.displayValue} 
                          size="small"
                          color={
                            item.score >= 0.9 ? 'success' : 
                            item.score >= 0.5 ? 'warning' : 'error'
                          }
                          sx={{ 
                            minWidth: 80,
                            fontWeight: 500,
                            flexShrink: 0
                          }}
                        />
                        {item.savings > 0 && (
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                            width: '100%',
                            minWidth: 0
                          }}>
                            <Typography 
                              variant="caption" 
                              noWrap
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500,
                                display: 'block',
                                textAlign: { xs: 'left', sm: 'right' }
                              }}
                            >
                              {item.savings > 1000 
                                ? `Savings: ${(item.savings / 1000).toFixed(1)}s`
                                : `Savings: ${item.savings.toFixed(0)}ms`
                              }
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </TabPanel>
      
      {/* Broken Links Tab Panel - index 2 */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h6" gutterBottom>Broken Links Check</Typography>
        {formatErrorMessage(brokenLinks)}
        
        {(!Array.isArray(brokenLinks) || brokenLinks.length === 0) ? (
          <Alert severity="success">No broken links detected!</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="broken links table">
              <TableHead>
                <TableRow>
                  <TableCell>Link URL</TableCell>
                  <TableCell>Link Text</TableCell>
                  <TableCell>Status Code</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brokenLinks.map((link, index) => (
                  <TableRow key={index} sx={{ 
                    bgcolor: link.error || (link.statusCode >= 400) 
                      ? 'error.lighter' 
                      : 'inherit' 
                  }}>
                    <TableCell>
                      <Link 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener"
                        sx={{
                          display: 'block',
                          maxWidth: '280px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {link.url}
                      </Link>
                    </TableCell>
                    <TableCell>{link.text || 'No text'}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small"
                        label={link.statusCode || 'Unknown'}
                        color={
                          !link.statusCode ? 'default' :
                          link.statusCode >= 500 ? 'error' :
                          link.statusCode >= 400 ? 'warning' :
                          link.statusCode >= 300 ? 'info' :
                          'success'
                        }
                      />
                    </TableCell>
                    <TableCell>{link.error || (link.statusCode >= 400 ? `HTTP ${link.statusCode}` : 'None')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>
      
      {/* Script Issues Tab Panel - index 3 */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="h6" gutterBottom>Script Issues</Typography>
        {formatErrorMessage(scriptIssues)}
        
        {(!Array.isArray(scriptIssues) || scriptIssues.length === 0) ? (
          <Alert severity="success">No script issues detected!</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="script issues table">
              <TableHead>
                <TableRow>
                  <TableCell>Script URL</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scriptIssues.map((issue, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>
                        {issue.url ? (
                          <Tooltip title={issue.url} placement="top">
                            <Link 
                              href={issue.url} 
                              target="_blank" 
                              rel="noopener"
                              sx={{
                                display: 'block',
                                maxWidth: '280px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {issue.url}
                            </Link>
                          </Tooltip>
                        ) : 'Unknown source'}
                      </TableCell>
                      <TableCell>{issue.type || issue.issueType || 'Unknown'}</TableCell>
                      <TableCell>
                        {issue.size ? `${(issue.size/1024).toFixed(1)} KB` : 'Unknown'}
                      </TableCell>
                      <TableCell>{issue.description || ''}</TableCell>
                    </TableRow>
                    {/* Show breakdown for multiple scripts if available */}
                    {issue.dependencies && issue.dependencies.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0 }}>
                          <Collapse in={true}>
                            <Box sx={{ m: 1, ml: 4 }}>
                              <Typography variant="subtitle2" gutterBottom component="div">
                                Script Dependencies
                              </Typography>
                              <Table size="small" aria-label="dependencies">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>URL</TableCell>
                                    <TableCell>Size</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {issue.dependencies.map((dep, depIndex) => (
                                    <TableRow key={depIndex}>
                                      <TableCell>
                                        <Tooltip title={dep.url} placement="top">
                                          <Link 
                                            href={dep.url} 
                                            target="_blank" 
                                            rel="noopener"
                                            sx={{
                                              display: 'block',
                                              maxWidth: '280px',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {dep.url}
                                          </Link>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>
                                        {dep.size ? `${(dep.size/1024).toFixed(1)} KB` : 'Unknown'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>
      
      {/* Image Issues Tab Panel - index 4 */}
      <TabPanel value={activeTab} index={4}>
        <Typography variant="h6" gutterBottom>Image Optimization Issues</Typography>
        {formatErrorMessage(imageIssues)}
        
        {(!Array.isArray(imageIssues) || imageIssues.length === 0) ? (
          <Alert severity="success">No image optimization issues detected!</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="image issues table">
              <TableHead>
                <TableRow>
                  <TableCell>Thumbnail</TableCell>
                  <TableCell>Image URL</TableCell>
                  <TableCell>Issue Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Dimensions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imageIssues.map((issue, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {issue.url && (
                        <Box 
                          sx={{ 
                            width: 80, 
                            height: 80, 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <img
                            src={issue.url}
                            alt="Thumbnail"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                            }}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {issue.url ? (
                        <Tooltip title={issue.url} placement="top">
                          <Link 
                            href={issue.url} 
                            target="_blank" 
                            rel="noopener"
                            sx={{
                              display: 'block',
                              maxWidth: '280px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {issue.url}
                          </Link>
                        </Tooltip>
                      ) : 'Unknown image'}
                    </TableCell>
                    <TableCell>{issue.issueType || issue.type || 'Optimization'}</TableCell>
                    <TableCell>{issue.size ? `${(issue.size/1024).toFixed(1)} KB` : 'Unknown'}</TableCell>
                    <TableCell>
                      {issue.width && issue.height ? `${issue.width}×${issue.height}` : 'Unknown'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>
      
      {/* Timeline Tab Panel - index 5 */}
      <TabPanel value={activeTab} index={5}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Performance Timeline
            <Tooltip title="Visual progress of page load">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Typography>
          
          {!audits['screenshot-thumbnails'] ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'info.light', 
                color: 'info.dark',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                No timeline information available.
              </Typography>
            </Paper>
          ) : (
            <Box>
              <Grid container spacing={2}>
                {audits['screenshot-thumbnails']?.details?.items?.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Box 
                        sx={{ 
                          position: 'relative',
                          pt: '56.25%', // 16:9 aspect ratio
                          backgroundColor: 'grey.100'
                        }}
                      >
                        <img 
                          src={item.data} 
                          alt={`Timeline ${index + 1}`} 
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {index === 0 ? 'Initial Paint' : `Frame ${index + 1}`}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <AccessTimeIcon fontSize="small" />
                          {formatTime(item.timing)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              {/* Timeline Legend */}
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Timeline Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: 'success.main' 
                        }} 
                      />
                      <Typography variant="body2">
                        FCP: {formatTime(metrics['first-contentful-paint']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: 'warning.main' 
                        }} 
                      />
                      <Typography variant="body2">
                        LCP: {formatTime(metrics['largest-contentful-paint']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: 'info.main' 
                        }} 
                      />
                      <Typography variant="body2">
                        Speed Index: {formatTime(metrics['speed-index']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: 'error.main' 
                        }} 
                      />
                      <Typography variant="body2">
                        TTI: {formatTime(metrics['interactive']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      </TabPanel>
      
      {/* Screenshot Tab Panel - index 6 */}
      <TabPanel value={activeTab} index={6}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Final Screenshot
            <Tooltip title="Final rendered state of the page">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Typography>
          
          {!audits['final-screenshot'] ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'info.light', 
                color: 'info.dark',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                No screenshot available.
              </Typography>
            </Paper>
          ) : (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    height: 0,
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    backgroundColor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <img 
                    src={audits['final-screenshot']?.details?.data} 
                    alt="Final Screenshot" 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Screenshot Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Captured at: {formatTime(audits['final-screenshot']?.timing || 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Viewport: {audits['final-screenshot']?.details?.width || 0} × {audits['final-screenshot']?.details?.height || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              
              {/* Performance Summary */}
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Performance Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        First Paint
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatTime(metrics['first-contentful-paint']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Largest Paint
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatTime(metrics['largest-contentful-paint']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Layout Shift
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {metrics['cumulative-layout-shift']?.displayValue}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Blocking Time
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatTime(metrics['total-blocking-time']?.numericValue)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      </TabPanel>
      
      {/* Diagnostics Tab Panel - index 7 */}
      <TabPanel value={activeTab} index={7}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Diagnostics Information
            <Tooltip title="Detailed technical information about your page">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Typography>
          
          {diagnostics.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'info.light', 
                color: 'info.dark',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                No diagnostic information available.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ mt: 2 }}>
              {diagnostics.map((item, index) => (
                <Accordion
                  key={item.id}
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    mb: 1,
                    '&:before': { display: 'none' },
                    '&:first-of-type': {
                      borderRadius: '8px !important',
                    },
                    '&:last-of-type': {
                      borderRadius: '8px !important',
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: '8px !important',
                      '&.Mui-expanded': {
                        borderBottom: 1,
                        borderBottomColor: 'divider'
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      width: '100%',
                      pr: 2
                    }}>
                      <Typography 
                        variant="subtitle1"
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          flex: 1
                        }}
                      >
                        {item.title}
                      </Typography>
                      {item.displayValue && (
                        <Chip 
                          label={item.displayValue} 
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 3, pb: 2 }}>
                    <Box>
                      <Typography 
                        variant="body2" 
                        component="div"
                        color="text.secondary"
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          '& a': {
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }
                        }}
                      >
                        {item.description}
                      </Typography>
                      
                      {/* Show details if available */}
                      {item.details?.items?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Additional Details:
                          </Typography>
                          <Box 
                            sx={{ 
                              maxHeight: 200, 
                              overflowY: 'auto',
                              px: 1
                            }}
                          >
                            {item.details.items.map((detail, idx) => (
                              <Typography 
                                key={idx} 
                                variant="caption" 
                                component="div"
                                sx={{ 
                                  mb: 0.5,
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <span style={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  backgroundColor: theme.palette.primary.main,
                                  display: 'inline-block'
                                }} />
                                {detail.url || detail.source || detail.location || JSON.stringify(detail)}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </TabPanel>
      
      {/* Passed Audits Tab Panel - index 8 */}
      <TabPanel value={activeTab} index={8}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Passed Audits
            <Tooltip title="Tests that your site has passed successfully">
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Typography>
          
          {passedAudits.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'warning.light', 
                color: 'warning.dark',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                No passed audits found.
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleOutlineIcon />
                  {passedAudits.length} Passed Tests
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  All tests below have passed Lighthouse's performance criteria
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {/* Group audits by category */}
                {Object.entries(
                  passedAudits.reduce((acc, audit) => {
                    const category = audit.category || 'other';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(audit);
                    return acc;
                  }, {})
                ).map(([category, audits]) => (
                  <Grid item xs={12} md={6} key={category}>
                    <Accordion
                      elevation={0}
                      defaultExpanded
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        mb: 2,
                        '&:before': { display: 'none' },
                        '&:first-of-type': {
                          borderRadius: '8px !important',
                        },
                        '&:last-of-type': {
                          borderRadius: '8px !important',
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          borderRadius: '8px !important',
                          '&.Mui-expanded': {
                            borderBottom: 1,
                            borderBottomColor: 'divider'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          >
                            {category}
                          </Typography>
                          <Chip 
                            label={audits.length} 
                            size="small" 
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2, pb: 1 }}>
                        <List disablePadding>
                          {audits.map((audit) => (
                            <ListItem
                              key={audit.id}
                              disableGutters
                              sx={{
                                py: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                '&:last-child': {
                                  borderBottom: 0
                                }
                              }}
                            >
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                  <CheckCircleOutlineIcon 
                                    color="success" 
                                    sx={{ 
                                      fontSize: 20,
                                      mt: 0.3
                                    }} 
                                  />
                                  <Typography 
                                    variant="subtitle2"
                                    sx={{ 
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      flex: 1,
                                      lineHeight: 1.4
                                    }}
                                  >
                                    {audit.title}
                                  </Typography>
                                </Box>
                                {audit.description && (
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                      ml: 3.5,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {audit.description}
                                  </Typography>
                                )}
                              </Box>
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </TabPanel>
      
      {/* Timestamp */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="textSecondary">
          Analysis time: {safeData.timestamp 
            ? new Date(safeData.timestamp).toLocaleString() 
            : new Date().toLocaleString()}
        </Typography>
        
        {safeData.stepTimings && Object.keys(safeData.stepTimings).length > 0 && (
          <Button 
            size="small" 
            onClick={() => setShowTimings(prev => !prev)}
            variant="text"
          >
            {showTimings ? 'Hide Analysis Timings' : 'Show Analysis Timings'}
          </Button>
        )}
      </Box>
      
      {/* Analysis Timings */}
      {showTimings && safeData.stepTimings && (
        <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Analysis Performance Metrics
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Analysis Step</TableCell>
                  <TableCell align="right">Duration</TableCell>
                  <TableCell align="right">% of Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(safeData.stepTimings)
                  .filter(([_, timing]) => timing.duration)
                  .sort((a, b) => {
                    const steps = ['initializing', 'fetching', 'analyzing', 'processing', 
                                  'technologies', 'issues', 'issues-links', 'issues-scripts', 
                                  'issues-images', 'finalizing'];
                    return steps.indexOf(a[0]) - steps.indexOf(b[0]);
                  })
                  .map(([step, timing]) => {
                    const stepNames = {
                      'initializing': 'Initialization',
                      'fetching': 'Lighthouse Analysis',
                      'analyzing': 'Performance Analysis',
                      'processing': 'Results Processing',
                      'technologies': 'Technology Detection',
                      'issues': 'Issue Detection Setup',
                      'issues-links': 'Link Checking',
                      'issues-scripts': 'Script Analysis',
                      'issues-images': 'Image Optimization',
                      'finalizing': 'Report Generation'
                    };
                    
                    const totalTime = safeData.totalTime || 
                      Object.values(safeData.stepTimings)
                        .reduce((sum, t) => sum + (t.duration || 0), 0);
                    
                    const percentage = totalTime ? (timing.duration / totalTime * 100).toFixed(1) : 0;
                    
                    return (
                      <TableRow key={step}>
                        <TableCell>{stepNames[step] || step}</TableCell>
                        <TableCell align="right">
                          {timing.duration < 1000 
                            ? `${timing.duration}ms` 
                            : `${(timing.duration / 1000).toFixed(1)}s`}
                        </TableCell>
                        <TableCell align="right">
                          {percentage}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                
                {/* Total row */}
                {safeData.totalTime && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Analysis Time</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {safeData.totalTime < 1000 
                        ? `${safeData.totalTime}ms` 
                        : `${(safeData.totalTime / 1000).toFixed(2)}s`}
                    </TableCell>
                    <TableCell align="right">100%</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Times are measured client-side and may vary based on network conditions and server load.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default LighthouseResults;